import type { SerializedNote } from './types';

const MAX_SHARE_NOTES = 150;

function isCompressionSupported(): boolean {
  return typeof CompressionStream !== 'undefined' && typeof DecompressionStream !== 'undefined';
}

function getShareableNotes(notes: SerializedNote[]): SerializedNote[] {
  if (notes.length <= MAX_SHARE_NOTES) return notes;
  return notes.slice(notes.length - MAX_SHARE_NOTES);
}

async function compressString(input: string): Promise<string> {
  if (!isCompressionSupported()) {
    return btoa(unescape(encodeURIComponent(input)));
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const stream = new CompressionStream('gzip');
  const writer = stream.writable.getWriter();
  writer.write(data);
  writer.close();

  const chunks: Uint8Array[] = [];
  const reader = stream.readable.getReader();
  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (result.value) chunks.push(result.value);
  }

  const compressed = new Uint8Array(chunks.reduce((acc, chunk) => acc + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    compressed.set(chunk, offset);
    offset += chunk.length;
  }

  let binary = '';
  for (let i = 0; i < compressed.length; i++) {
    binary += String.fromCharCode(compressed[i]);
  }
  return btoa(binary);
}

async function decompressString(input: string): Promise<string> {
  const binary = atob(input);
  const compressed = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    compressed[i] = binary.charCodeAt(i);
  }

  // Gzip magic bytes: 0x1f 0x8b
  if (compressed.length < 2 || compressed[0] !== 0x1f || compressed[1] !== 0x8b) {
    return decodeURIComponent(escape(binary));
  }

  if (!isCompressionSupported()) {
    throw new Error('Gzip-compressed share URL requires a browser that supports DecompressionStream');
  }

  const stream = new DecompressionStream('gzip');
  const writer = stream.writable.getWriter();
  writer.write(compressed);
  writer.close();

  const chunks: Uint8Array[] = [];
  const reader = stream.readable.getReader();
  let done = false;
  while (!done) {
    const result = await reader.read();
    done = result.done;
    if (result.value) chunks.push(result.value);
  }

  const decoder = new TextDecoder();
  let output = '';
  for (const chunk of chunks) {
    output += decoder.decode(chunk, { stream: true });
  }
  return output;
}

export async function encodeNotesToURL(notes: SerializedNote[]): Promise<string> {
  const toShare = getShareableNotes(notes);
  const json = JSON.stringify(toShare);
  const compressed = await compressString(json);
  return `${window.location.origin}${window.location.pathname}#shared=${compressed}`;
}

export async function decodeNotesFromURL(): Promise<SerializedNote[] | null> {
  const hash = window.location.hash;
  if (!hash.startsWith('#shared=')) return null;
  try {
    const encoded = hash.slice(8);
    const json = await decompressString(encoded);
    return JSON.parse(json) as SerializedNote[];
  } catch {
    return null;
  }
}

export async function copyShareURL(notes: SerializedNote[]): Promise<boolean> {
  const url = await encodeNotesToURL(notes);
  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    const input = document.createElement('input');
    input.value = url;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    return true;
  }
}
